import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { api } from '../../utils/apiUtils';

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch all pages in a workspace
 */
export const fetchPages = createAsyncThunk(
  'pages/fetchPages',
  async ({ workspaceId, archived = false, parent, search, filter, sortBy, sortOrder, includeDeleted, useCache = false, merge = false }, { rejectWithValue, getState }) => {
    try {
      // Use new unified endpoint
      const searchParams = new URLSearchParams();
      if (workspaceId && workspaceId !== 'undefined' && workspaceId !== 'null') {
        searchParams.append('workspaceId', workspaceId);
      }
      if (archived !== 'all') {
        searchParams.append('archived', archived);
      }
      if (includeDeleted) {
        searchParams.append('includeDeleted', includeDeleted);
      }

      if (parent !== undefined) {
        searchParams.append('parent', parent || 'null');
      }

      if (search) searchParams.append('search', search);
      if (filter) searchParams.append('filter', filter);
      if (sortBy) searchParams.append('sortBy', sortBy);
      if (sortOrder) searchParams.append('sortOrder', sortOrder);

      const url = `${config.API_URI}/v1/pages/all?${searchParams.toString()}`;

      const response = await api.get(url, {}, useCache, 3 * 60 * 1000); // Cache for 3 minutes

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      // Compute isFavorite and isShared for each page
      const pagesWithMeta = response.pages.map(page => {
        const ownerId = page.ownerUserId?._id || page.ownerUserId;
        return {
          ...page,
          isFavorite: page.favorite?.includes(userId) || false,
          isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
        };
      });

      return { workspaceId, pages: pagesWithMeta, userId, merge };
    } catch (error) {
      console.error('Error fetching pages:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch a single page by ID
 */
export const fetchPageById = createAsyncThunk(
  'pages/fetchPageById',
  async ({ pageId, useCache = false }, { rejectWithValue, getState }) => {
    try {
      const response = await api.get(
        `${config.API_URI}/v1/pages/${pageId}`,
        {},
        useCache,
        2 * 60 * 1000 // Cache for 2 minutes
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      // Compute isFavorite and isShared
      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      const page = {
        ...response.page,
        role: response.userRole, // Include role from backend
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };

      return page;
    } catch (error) {
      console.error('Error fetching page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create a new page
 */
export const createPage = createAsyncThunk(
  'pages/createPage',
  async ({ workspaceId, pageData }, { rejectWithValue, getState }) => {
    try {
      // Merge workspaceId into pageData for the new unified API
      const dataToSend = {
        ...pageData,
        workspaceId: workspaceId || pageData.workspaceId || null
      };

      const response = await api.post(
        `${config.API_URI}/v1/pages`,
        dataToSend,
        { noDedupe: true }
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      return {
        ...response.page,
        role: 'owner',
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error creating page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update a page
 */
export const updatePage = createAsyncThunk(
  'pages/updatePage',
  async ({ pageId, updates }, { rejectWithValue, getState }) => {
    try {
      const response = await api.put(
        `${config.API_URI}/v1/pages/${pageId}`,
        updates
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      return {
        ...response.page,
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error updating page:', error);
      return rejectWithValue(error.message);
    }
  }
);
/**
 * Delete a page
 */
export const deletePage = createAsyncThunk(
  'pages/deletePage',
  async ({ pageId }, { rejectWithValue }) => {
    try {
      await api.delete(`${config.API_URI}/v1/pages/${pageId}`);
      return { pageId };
    } catch (error) {
      console.error('Error deleting page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Restore a deleted page
 */
export const restorePage = createAsyncThunk(
  'pages/restorePage',
  async ({ pageId }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post(`${config.API_URI}/v1/pages/${pageId}/restore`);
      
      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      return {
        ...response.page,
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error restoring page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Archive/Unarchive a page
 */
export const archivePage = createAsyncThunk(
  'pages/archivePage',
  async ({ pageId, archived }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post(
        `${config.API_URI}/v1/pages/${pageId}/archive`,
        { archived }
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      if (!response?.page) throw new Error('Invalid response from server');
      const pageData = response.page;
      const ownerId = pageData.ownerUserId?._id || pageData.ownerUserId;
      return {
        ...pageData,
        isFavorite: pageData.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error archiving page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Move a page (change parent)
 */
export const movePage = createAsyncThunk(
  'pages/movePage',
  async ({ pageId, newParent }, { rejectWithValue, getState }) => {
    try {
      const response = await api.put(
        `${config.API_URI}/v1/pages/${pageId}/move`,
        { newParentId: newParent }
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      return {
        ...response.page,
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error moving page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Reorder pages
 */
export const reorderPages = createAsyncThunk(
  'pages/reorderPages',
  async ({ pageId, newOrder, section }, { rejectWithValue, getState }) => {
    try {
      await api.put(
        `${config.API_URI}/v1/pages/${pageId}/reorder`,
        { newOrder, section }
      );

      // Return pageId and newOrder for the reducer
      // Also get the current page to update it optimistically
      const state = getState();
      const page = state.pages.byId[pageId];

      return {
        pageId,
        newOrder,
        section,
        page: { 
          ...page, 
          [section === 'favorites' ? 'favoriteOrder' : 'order']: newOrder 
        }
      };
    } catch (error) {
      console.error('Error reordering page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update personal page settings (expansion, etc.)
 */
export const updatePageUserSettings = createAsyncThunk(
  'pages/updatePageUserSettings',
  async ({ pageId, isExpanded, order }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${config.API_URI}/v1/pages/${pageId}/settings`,
        { isExpanded, order }
      );
      return { pageId, ...response.data.settings };
    } catch (error) {
      console.error('Error updating page settings:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Duplicate a page
 */
export const duplicatePage = createAsyncThunk(
  'pages/duplicatePage',
  async ({ pageId }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post(
        `${config.API_URI}/v1/pages/${pageId}/duplicate`
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      if (!response?.page) throw new Error('Invalid response from server');
      const pageData = response.page;
      const ownerId = pageData.ownerUserId?._id || pageData.ownerUserId;
      return {
        ...pageData,
        isFavorite: pageData.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error duplicating page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Permanently delete a page
 */
export const permanentDeletePage = createAsyncThunk(
  'pages/permanentDeletePage',
  async ({ pageId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `${config.API_URI}/v1/pages/${pageId}/permanent`
      );
      return pageId;
    } catch (error) {
      console.error('Error permanently deleting page:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Toggle favorite
 */
export const toggleFavorite = createAsyncThunk(
  'pages/toggleFavorite',
  async ({ pageId }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post(
        `${config.API_URI}/v1/pages/${pageId}/favorite`
      );

      // Get userId from state
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      return { pageId, favorited: response.favorited, userId };
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Add member to page
 */
export const addPageMember = createAsyncThunk(
  'pages/addPageMember',
  async ({ pageId, userIdToAdd, role }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${config.API_URI}/v1/pages/${pageId}/members`,
        { userIdToAdd, role }
      );
      return { pageId, members: response.members };
    } catch (error) {
      console.error('Error adding page member:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Remove member from page
 */
export const removePageMember = createAsyncThunk(
  'pages/removePageMember',
  async ({ pageId, memberId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `${config.API_URI}/v1/pages/${pageId}/members/${memberId}`
      );
      return { pageId, memberId };
    } catch (error) {
      console.error('Error removing page member:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update page visibility
 */
export const updatePageVisibility = createAsyncThunk(
  'pages/updatePageVisibility',
  async ({ pageId, visibility }, { rejectWithValue, getState }) => {
    try {
      const response = await api.put(
        `${config.API_URI}/v1/pages/${pageId}/visibility`,
        { visibility }
      );

      // Get userId from state to compute isFavorite
      const state = getState();
      const userState = state.user?.user;
      const userId = userState?.user?._id || userState?.user?.id || userState?._id || userState?.id;

      const ownerId = response.page.ownerUserId?._id || response.page.ownerUserId;
      return {
        ...response.page,
        isFavorite: response.page.favorite?.includes(userId) || false,
        isShared: !!userId && !!ownerId && ownerId.toString() !== userId.toString()
      };
    } catch (error) {
      console.error('Error updating page visibility:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Upload cover image for a page
 */
export const uploadPageCoverImage = createAsyncThunk(
  'pages/uploadCoverImage',
  async ({ pageId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${config.API_URI}/v1/pages/${pageId}/cover`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || response.statusText || 'Failed to upload cover image';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { pageId, coverImage: data.coverImage };
    } catch (error) {
      console.error('Error uploading cover image:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update cover image position
 */
export const updatePageCoverPosition = createAsyncThunk(
  'pages/updateCoverPosition',
  async ({ pageId, position }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${config.API_URI}/v1/pages/${pageId}/cover/position`,
        { position }
      );
      return { pageId, coverImage: response.coverImage };
    } catch (error) {
      console.error('Error updating cover position:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Remove cover image from a page
 */
export const removePageCoverImage = createAsyncThunk(
  'pages/removeCoverImage',
  async ({ pageId }, { rejectWithValue }) => {
    try {
      await api.delete(`${config.API_URI}/v1/pages/${pageId}/cover`);
      return { pageId };
    } catch (error) {
      console.error('Error removing cover image:', error);
      return rejectWithValue(error.message);
    }
  }
);

// ============================================
// Slice
// ============================================

const pageSlice = createSlice({
  name: 'pages',
  initialState: {
    // Normalized data
    byId: {},
    allIds: [],
    byWorkspace: {},
    pages: [], // List of pages for PageList

    // Favorites and recent (user-specific)
    favorites: [],
    recent: [],

    // Current page being viewed
    currentPage: null,

    // UI state
    loading: false,
    listLoading: false,
    saving: false,
    error: null,

    // Collaboration
    viewers: {}, // { pageId: [userId1, userId2] }
    editors: {}, // { pageId: [userId1] }
  },
  reducers: {
    // Set current page
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    // Clear current page
    clearCurrentPage: (state) => {
      state.currentPage = null;
    },

    // Optimistic update for page content
    updatePageContentOptimistic: (state, action) => {
      const { pageId, content } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].content = content;
        state.byId[pageId].updatedAt = new Date().toISOString();
      }
    },

    // Optimistic update for page move
    updatePageMoveOptimistic: (state, action) => {
      const { pageId, newParent } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          parentId: newParent,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page reorder
    updatePageReorderOptimistic: (state, action) => {
      const { pageId, newOrder } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          order: newOrder,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page title
    updatePageTitleOptimistic: (state, action) => {
      const { pageId, title } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          title,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page icon
    updatePageIconOptimistic: (state, action) => {
      const { pageId, icon } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          icon,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page cover
    updatePageCoverOptimistic: (state, action) => {
      const { pageId, coverImage } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          coverImage,
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page favorite
    updatePageFavoriteOptimistic: (state, action) => {
      const { pageId, isFavorite, userId } = action.payload;
      if (state.byId[pageId]) {
        const page = state.byId[pageId];

        // Update favorites list
        if (isFavorite) {
          if (!state.favorites.includes(pageId)) {
            state.favorites.push(pageId);
          }
        } else {
          state.favorites = state.favorites.filter(id => id !== pageId);
        }

        // Update the page object's favorite array
        state.byId[pageId] = {
          ...page,
          isFavorite,
          favorite: isFavorite && userId
            ? [...(page.favorite || []), userId]
            : (page.favorite || []).filter(id => id !== userId),
          updatedAt: new Date().toISOString()
        };
      }
    },

    // Optimistic update for page restore
    updatePageRestoreOptimistic: (state, action) => {
      const { pageId } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].deletedAt = null;
        state.byId[pageId].updatedAt = new Date().toISOString();
      }
      // Also update pages array
      state.pages = state.pages.map(p => 
        p.shortId === pageId ? { ...p, deletedAt: null } : p
      );
    },

    // Add viewer to page
    addViewer: (state, action) => {
      const { pageId, userId } = action.payload;
      if (!state.viewers[pageId]) {
        state.viewers[pageId] = [];
      }
      if (!state.viewers[pageId].includes(userId)) {
        state.viewers[pageId].push(userId);
      }
    },

    // Remove viewer from page
    removeViewer: (state, action) => {
      const { pageId, userId } = action.payload;
      if (state.viewers[pageId]) {
        state.viewers[pageId] = state.viewers[pageId].filter(id => id !== userId);
      }
    },

    // Add editor to page
    addEditor: (state, action) => {
      const { pageId, userId } = action.payload;
      if (!state.editors[pageId]) {
        state.editors[pageId] = [];
      }
      if (!state.editors[pageId].includes(userId)) {
        state.editors[pageId].push(userId);
      }
    },

    // Remove editor from page
    removeEditor: (state, action) => {
      const { pageId, userId } = action.payload;
      if (state.editors[pageId]) {
        state.editors[pageId] = state.editors[pageId].filter(id => id !== userId);
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Socket reducers for real-time updates
    addPage: (state, action) => {
      const page = action.payload;
      if (!state.byId[page.shortId]) {
        state.byId[page.shortId] = page;
        state.allIds.push(page.shortId);

        // Add to workspace pages
        const workspaceId = page.workspace?.shortId || page.workspace;
        if (workspaceId) {
          if (!state.byWorkspace[workspaceId]) {
            state.byWorkspace[workspaceId] = [];
          }
          if (!state.byWorkspace[workspaceId].includes(page.shortId)) {
            state.byWorkspace[workspaceId].push(page.shortId);
          }
        }
      }
    },

    updatePageFromSocket: (state, action) => {
      const page = action.payload;
      if (state.byId[page.shortId]) {
        state.byId[page.shortId] = { ...state.byId[page.shortId], ...page };
      }
    },

    removePage: (state, action) => {
      const pageId = action.payload;
      if (state.byId[pageId]) {
        const page = state.byId[pageId];
        const workspaceId = page.workspace?.shortId || page.workspace;

        // Remove from byId
        delete state.byId[pageId];

        // Remove from allIds
        state.allIds = state.allIds.filter(id => id !== pageId);

        // Remove from byWorkspace
        if (workspaceId && state.byWorkspace[workspaceId]) {
          state.byWorkspace[workspaceId] = state.byWorkspace[workspaceId].filter(id => id !== pageId);
        }

        // Remove from favorites
        state.favorites = state.favorites.filter(id => id !== pageId);

        // Remove from recent
        state.recent = state.recent.filter(id => id !== pageId);

        // Clear if it's the current page
        if (state.currentPage === pageId) {
          state.currentPage = null;
        }
      }
    },
    // Update page public status
    setPagePublicStatus: (state, action) => {
      const { pageId, isPublic, publicShareToken } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId] = {
          ...state.byId[pageId],
          isPublic,
          publicShareToken: isPublic ? publicShareToken : undefined,
          updatedAt: new Date().toISOString()
        };
      }
      // Also update pages array if it exists
      if (state.pages) {
        state.pages = state.pages.map(p => 
          p.shortId === pageId ? { ...p, isPublic, publicShareToken: isPublic ? publicShareToken : undefined } : p
        );
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch pages (list) - uses listLoading to avoid affecting individual page views
    builder.addCase(fetchPages.pending, (state, action) => {
      state.listLoading = true;
      state.error = null;
      // ONLY clear pages if we're not merging (full reload)
      // Preserve byId entries that have content (individually-fetched pages)
      // so the currently viewed page in the editor is not disrupted
      if (!action.meta.arg?.merge) {
        state.pages = [];
        // Preserve the currently viewed page and any pages with full content
        const preservedById = {};
        const preservedIds = [];
        if (state.currentPage && state.byId[state.currentPage]) {
          preservedById[state.currentPage] = state.byId[state.currentPage];
          preservedIds.push(state.currentPage);
        }
        state.byId = preservedById;
        state.allIds = preservedIds;
      }
    });
    builder.addCase(fetchPages.fulfilled, (state, action) => {
      state.listLoading = false;
      const { workspaceId, pages, merge } = action.payload;

      // Normalize pages - merge carefully to avoid overwriting individually-fetched data
      pages.forEach(page => {
        const existing = state.byId[page.shortId];
        if (existing && existing.content !== undefined) {
          // Preserve content and role from individually-fetched page data
          state.byId[page.shortId] = { ...page, content: existing.content, role: existing.role };
        } else {
          state.byId[page.shortId] = page;
        }
        if (!state.allIds.includes(page.shortId)) {
          state.allIds.push(page.shortId);
        }
      });

      // Store results
      if (merge) {
        // Merge with existing pages, avoiding duplicates
        const existingShortIds = new Set(state.pages.map(p => p.shortId));
        const newPages = pages.filter(p => !existingShortIds.has(p.shortId));
        state.pages = [...state.pages, ...newPages];

        // Update workspace mapping
        if (!state.byWorkspace[workspaceId]) state.byWorkspace[workspaceId] = [];
        const existingWorkspaceIds = new Set(state.byWorkspace[workspaceId]);
        const newWorkspaceIds = pages.map(p => p.shortId).filter(id => !existingWorkspaceIds.has(id));
        state.byWorkspace[workspaceId] = [...state.byWorkspace[workspaceId], ...newWorkspaceIds];
      } else {
        // Full replacement
        state.pages = pages;
        state.byWorkspace[workspaceId] = pages.map(p => p.shortId);
      }
    });
    builder.addCase(fetchPages.rejected, (state, action) => {
      state.listLoading = false;
      state.error = action.payload;
    });

    // Fetch page by ID
    builder.addCase(fetchPageById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPageById.fulfilled, (state, action) => {
      state.loading = false;
      const page = action.payload;
      state.byId[page.shortId] = page;
      if (!state.allIds.includes(page.shortId)) {
        state.allIds.push(page.shortId);
      }
      state.currentPage = page.shortId;
    });
    builder.addCase(fetchPageById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create page
    builder.addCase(createPage.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(createPage.fulfilled, (state, action) => {
      state.saving = false;
      const page = action.payload;
      state.byId[page.shortId] = page;
      state.allIds.push(page.shortId);

      // Add to workspace pages
      const workspaceId = page.workspace.shortId || page.workspace;
      if (!state.byWorkspace[workspaceId]) {
        state.byWorkspace[workspaceId] = [];
      }
      state.byWorkspace[workspaceId].push(page.shortId);
    });
    builder.addCase(createPage.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload;
    });

    // Update page
    builder.addCase(updatePage.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(updatePage.fulfilled, (state, action) => {
      state.saving = false;
      const page = action.payload;
      if (state.byId[page.shortId]) {
        state.byId[page.shortId] = { ...state.byId[page.shortId], ...page };
      } else {
        state.byId[page.shortId] = page;
      }
    });
    builder.addCase(updatePage.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload;
    });

    // Delete page (soft delete)
    builder.addCase(deletePage.fulfilled, (state, action) => {
      const { pageId } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].deletedAt = new Date().toISOString();
      }
      // Update the flat pages array too
      state.pages = state.pages.map(p => 
        p.shortId === pageId ? { ...p, deletedAt: new Date().toISOString() } : p
      );

      // Clear current page if it's the deleted one
      if (state.currentPage === pageId) {
        state.currentPage = null;
      }
    });

    // Permanent delete page
    builder.addCase(permanentDeletePage.fulfilled, (state, action) => {
      const pageId = action.payload;
      delete state.byId[pageId];
      state.allIds = state.allIds.filter(id => id !== pageId);
      state.favorites = state.favorites.filter(id => id !== pageId);
      state.recent = state.recent.filter(id => id !== pageId);
      
      // Remove from all workspace arrays
      Object.keys(state.byWorkspace).forEach(workspaceId => {
        state.byWorkspace[workspaceId] = state.byWorkspace[workspaceId].filter(id => id !== pageId);
      });
    });

    // Restore page
    builder.addCase(restorePage.fulfilled, (state, action) => {
      const page = action.payload;
      state.byId[page.shortId] = page;
      if (!state.allIds.includes(page.shortId)) {
        state.allIds.push(page.shortId);
      }
      // Update or add to flat pages array
      const index = state.pages.findIndex(p => p.shortId === page.shortId);
      if (index !== -1) {
        state.pages[index] = page;
      } else {
        state.pages.push(page);
      }
    });

    // Archive page
    builder.addCase(archivePage.fulfilled, (state, action) => {
      const page = action.payload;
      if (state.byId[page.shortId]) {
        state.byId[page.shortId] = { ...state.byId[page.shortId], ...page };
      } else {
        state.byId[page.shortId] = page;
      }
    });

    // Move page
    builder.addCase(movePage.fulfilled, (state, action) => {
      const page = action.payload;
      if (state.byId[page.shortId]) {
        state.byId[page.shortId] = { ...state.byId[page.shortId], ...page };
      } else {
        state.byId[page.shortId] = page;
      }
    });
    // Update page user settings
    builder.addCase(updatePageUserSettings.fulfilled, (state, action) => {
      const { pageId, isExpanded, order } = action.payload;
      // We need to look up the page in our store. action.payload.pageId might be an ID or shortId.
      const page = Object.values(state.byId).find(p => p._id === pageId || p.shortId === pageId);
      if (page) {
        if (isExpanded !== undefined) page.isExpanded = isExpanded;
        if (order !== undefined) page.order = order;
      }
    });

    // Reorder pages
    builder.addCase(reorderPages.fulfilled, (state, action) => {
      const { pageId, newOrder, section, page } = action.payload;
      if (state.byId[pageId]) {
        // Update the entire page object to ensure UI updates
        const updateField = section === 'favorites' ? 'favoriteOrder' : 'order';
        state.byId[pageId] = { ...state.byId[pageId], ...page, [updateField]: newOrder };
      }
    });

    // Duplicate page
    builder.addCase(duplicatePage.fulfilled, (state, action) => {
      const page = action.payload;
      state.byId[page.shortId] = page;
      if (!state.allIds.includes(page.shortId)) {
        state.allIds.push(page.shortId);
      }

      // Add to workspace pages if it's a workspace page
      const workspaceId = page.workspaceId?.shortId || page.workspaceId || page.workspace?.shortId || page.workspace;
      if (workspaceId && state.byWorkspace[workspaceId]) {
        if (!state.byWorkspace[workspaceId].includes(page.shortId)) {
          state.byWorkspace[workspaceId].push(page.shortId);
        }
      }
    });

    // Toggle favorite
    builder.addCase(toggleFavorite.fulfilled, (state, action) => {
      const { pageId, favorited, userId } = action.payload;

      // Update favorites list
      if (favorited) {
        if (!state.favorites.includes(pageId)) {
          state.favorites.push(pageId);
        }
      } else {
        state.favorites = state.favorites.filter(id => id !== pageId);
      }

      // Update the page object's favorite array to trigger UI updates
      if (state.byId[pageId] && userId) {
        const page = state.byId[pageId];
        // Create a new page object to ensure React detects the change
        state.byId[pageId] = {
          ...page,
          favorite: favorited
            ? [...(page.favorite || []), userId]
            : (page.favorite || []).filter(id => id !== userId)
        };
      }
    });

    // Update page visibility
    builder.addCase(updatePageVisibility.fulfilled, (state, action) => {
      const page = action.payload;
      if (state.byId[page.shortId]) {
        state.byId[page.shortId] = { ...state.byId[page.shortId], ...page };
      } else {
        state.byId[page.shortId] = page;
      }
    });

    // Upload cover image
    builder.addCase(uploadPageCoverImage.fulfilled, (state, action) => {
      const { pageId, coverImage } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].coverImage = coverImage;
      }
    });

    // Update cover position
    builder.addCase(updatePageCoverPosition.fulfilled, (state, action) => {
      const { pageId, coverImage } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].coverImage = coverImage;
      }
    });

    // Remove cover image
    builder.addCase(removePageCoverImage.fulfilled, (state, action) => {
      const { pageId } = action.payload;
      if (state.byId[pageId]) {
        state.byId[pageId].coverImage = null;
      }
    });
  },
});

export const {
  setCurrentPage,
  clearCurrentPage,
  updatePageContentOptimistic,
  updatePageMoveOptimistic,
  updatePageReorderOptimistic,
  updatePageTitleOptimistic,
  updatePageIconOptimistic,
  updatePageCoverOptimistic,
  updatePageFavoriteOptimistic,
  updatePageRestoreOptimistic,
  addViewer,
  removeViewer,
  addEditor,
  removeEditor,
  clearError,
  updatePageFromSocket,
  removePage,
  addPage,
  setPagePublicStatus,
} = pageSlice.actions;

export default pageSlice.reducer;

